import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection
} from '@angular/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  RippleGlobalOptions
} from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { routes } from '@app/app.routes';
import { CustomDateAdapter } from '@app/shared/configs/custom-date-adapter';
import { DATE_PIPE_DEFAULTS, MAT_DEFAULT_DATE_FORMATS } from '@app/shared/configs/date-format.config';
import { ProjectParamRouteReuseStrategy } from '@app/shared/routing/project-param-route-reuse.strategy';
import { LanguageService } from '@app/shared/services/shared/language.service';
import { environment } from '@env/environment';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { API_BASE_ENDPOINT, apiBaseInterceptor } from './shared/interceptors/api-base.interceptor';
import { authInterceptor } from './shared/interceptors/auth-interceptor';
import { httpErrorInterceptor } from './shared/interceptors/error-interceptor';
import { I18nMatPaginatorIntl } from './shared/utils/i18n-mat-paginator-intl';

const MAT_FORM_FIELD_OPTIONS = {
  appearance: 'outline',
  color: 'primary',
  subscriptSizing: 'dynamic'
};

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 350,
    exitDuration: 0
  },
  terminateOnPointerUp: false
};

const MAT_BUTTON_TOGGLE_OPTIONS = {
  hideSingleSelectionIndicator: true,
  hideMultipleSelectionIndicator: true
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withXhr(), withInterceptors([httpErrorInterceptor, authInterceptor, apiBaseInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: 'i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en-US',
      lang: 'en-US'
    }),
    provideRouter(routes /* , withDebugTracing() */),
    { provide: RouteReuseStrategy, useClass: ProjectParamRouteReuseStrategy },
    { provide: MatPaginatorIntl, useClass: I18nMatPaginatorIntl },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: MAT_BUTTON_TOGGLE_OPTIONS },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DEFAULT_DATE_FORMATS },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: MAT_FORM_FIELD_OPTIONS },
    {
      provide: LOCALE_ID,
      useFactory: (): string => inject(LanguageService).currentLocale()
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (): string => inject(LanguageService).currentLocale()
    },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: DATE_PIPE_DEFAULTS },
    { provide: API_BASE_ENDPOINT, useValue: environment.APIEndPoint },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: 'dialog' }
    },
    DatePipe
  ]
};
